import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Download, 
  Mail, 
  Share2, 
  ChevronDown, 
  ChevronUp,
  Star,
  AlertTriangle,
  Heart,
  Sparkles,
  BookOpen,
  Loader2,
  CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ConstellationChart from "../components/ConstellationChart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARCHETYPE_ICONS = {
  critico_interior: "⚖️",
  nino_herido: "💔",
  protector: "🛡️",
  complacedor: "🤝",
  guerrero: "⚔️",
  sabio: "📜",
  nino_libre: "✨",
  cuidador: "💚",
  perfeccionista: "🎯",
  rebelde: "🔥",
  amante: "❤️",
  ermitano: "🏔️",
  estratega: "🧠",
  sanador: "🌿",
  self: "☀️"
};

const ARCHETYPE_COLORS = {
  critico_interior: "#C62828",
  nino_herido: "#5C6BC0",
  protector: "#37474F",
  complacedor: "#F06292",
  guerrero: "#D32F2F",
  sabio: "#7E57C2",
  nino_libre: "#FFD54F",
  cuidador: "#81C784",
  perfeccionista: "#00897B",
  rebelde: "#F57C00",
  amante: "#E91E63",
  ermitano: "#78909C",
  estratega: "#5E35B1",
  sanador: "#26A69A",
  self: "#FFC107"
};

export default function ResultadosPage() {
  const { resultadoId } = useParams();
  const [resultado, setResultado] = useState(null);
  const [arquetiposData, setArquetiposData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try localStorage first
        const cached = localStorage.getItem(`resultado_${resultadoId}`);
        if (cached) {
          setResultado(JSON.parse(cached));
        } else if (resultadoId) {
          const response = await axios.get(`${API}/resultado/${resultadoId}`);
          setResultado(response.data);
        }

        // Fetch archetype details
        const arqResponse = await axios.get(`${API}/arquetipos`);
        const arqMap = {};
        arqResponse.data.forEach(arq => {
          arqMap[arq.id] = arq;
        });
        setArquetiposData(arqMap);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar los resultados");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [resultadoId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveResult = async () => {
    if (!email) {
      toast.error("Por favor ingresa tu email");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/guardar-resultado`, {
        email,
        resultado_id: resultado.id
      });
      toast.success("¡Resultado guardado! Te enviaremos el link de acceso.");
    } catch (error) {
      console.error("Error saving result:", error);
      toast.error("Error al guardar el resultado");
    } finally {
      setSaving(false);
    }
  };

  const generatePDF = async () => {
    setGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (height) => {
        if (yPosition + height > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Title Page
      pdf.setFillColor(15, 5, 24);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setTextColor(243, 229, 245);
      pdf.setFontSize(24);
      pdf.text('Tu Ecosistema de', pageWidth / 2, 60, { align: 'center' });
      pdf.setFontSize(28);
      pdf.setTextColor(0, 172, 193);
      pdf.text('Arquetipos Internos', pageWidth / 2, 75, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setTextColor(209, 196, 233);
      pdf.text('Alquimia del Alma', pageWidth / 2, 100, { align: 'center' });
      pdf.text(new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth / 2, 110, { align: 'center' });

      // New page for results
      pdf.addPage();
      yPosition = margin;
      
      pdf.setFillColor(15, 5, 24);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Section: Main Archetypes
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.text('Tus Arquetipos Principales', margin, yPosition);
      yPosition += 15;

      resultado.arquetipos_principales.forEach((arq, index) => {
        const arqData = arquetiposData[arq.arquetipo];
        if (!arqData) return;

        checkPageBreak(40);
        
        pdf.setFontSize(14);
        pdf.setTextColor(0, 172, 193);
        pdf.text(`${index + 1}. ${arqData.nombre}`, margin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setTextColor(209, 196, 233);
        const nivel = arq.nivel === 'muy_alto' ? 'Muy Alto' : arq.nivel === 'alto' ? 'Alto' : 'Moderado';
        pdf.text(`Nivel: ${nivel} (${arq.porcentaje}%)`, margin, yPosition);
        yPosition += 5;

        // Progress bar
        pdf.setFillColor(26, 11, 46);
        pdf.rect(margin, yPosition, 100, 4, 'F');
        const color = ARCHETYPE_COLORS[arq.arquetipo] || '#6A1B9A';
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        pdf.setFillColor(r, g, b);
        pdf.rect(margin, yPosition, arq.porcentaje, 4, 'F');
        yPosition += 12;

        // Description (truncated)
        pdf.setFontSize(9);
        pdf.setTextColor(149, 117, 205);
        const desc = arqData.descripcion.substring(0, 200) + '...';
        const lines = pdf.splitTextToSize(desc, pageWidth - margin * 2);
        pdf.text(lines, margin, yPosition);
        yPosition += lines.length * 4 + 10;
      });

      // Conflicts section
      if (resultado.conflictos_principales?.length > 0) {
        pdf.addPage();
        yPosition = margin;
        pdf.setFillColor(15, 5, 24);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.text('Conflictos Internos Identificados', margin, yPosition);
        yPosition += 15;

        resultado.conflictos_principales.forEach((conflicto, index) => {
          checkPageBreak(30);
          
          const arq1 = arquetiposData[conflicto.polo1];
          const arq2 = arquetiposData[conflicto.polo2];
          
          pdf.setFontSize(12);
          pdf.setTextColor(216, 67, 21);
          pdf.text(`${arq1?.nombre || conflicto.polo1} vs ${arq2?.nombre || conflicto.polo2}`, margin, yPosition);
          yPosition += 6;
          
          pdf.setFontSize(9);
          pdf.setTextColor(209, 196, 233);
          const conflictLines = pdf.splitTextToSize(conflicto.descripcion, pageWidth - margin * 2);
          pdf.text(conflictLines, margin, yPosition);
          yPosition += conflictLines.length * 4 + 10;
        });
      }

      // Self level
      pdf.addPage();
      yPosition = margin;
      pdf.setFillColor(15, 5, 24);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.text('Acceso al Self', margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setTextColor(255, 193, 7);
      pdf.text(`Nivel actual: ${resultado.nivel_self}%`, margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setTextColor(209, 196, 233);
      const selfText = resultado.nivel_self >= 60 
        ? 'Tienes buen acceso a tu Self. Puedes observar tus arquetipos con relativa calma y claridad.'
        : resultado.nivel_self >= 40
        ? 'Tu acceso al Self es moderado. A veces logras observar tus partes, otras te fusionas con ellas.'
        : 'Tu acceso al Self necesita desarrollo. Tiendes a fusionarte con tus arquetipos, especialmente bajo estrés.';
      const selfLines = pdf.splitTextToSize(selfText, pageWidth - margin * 2);
      pdf.text(selfLines, margin, yPosition);
      yPosition += selfLines.length * 5 + 15;

      // Closing
      pdf.setFontSize(14);
      pdf.setTextColor(0, 172, 193);
      pdf.text('Recuerda:', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setTextColor(243, 229, 245);
      const closingText = '"No estás roto/a, estás completo/a. Todas estas voces son partes de ti buscando ser vistas y amadas."';
      const closingLines = pdf.splitTextToSize(closingText, pageWidth - margin * 2);
      pdf.text(closingLines, margin, yPosition);

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(149, 117, 205);
      pdf.text('Alquimia del Alma | agenda@alquimiadelalma.click | +57 300 554 2958', pageWidth / 2, pageHeight - 10, { align: 'center' });

      pdf.save('mi-ecosistema-arquetipos.pdf');
      toast.success("PDF generado exitosamente");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  if (!resultado) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-white mb-4">Resultado no encontrado</h1>
          <Link to="/test">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full">
              Hacer el Test
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" ref={reportRef}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-3xl sm:text-4xl text-white mb-4">
            Tu Ecosistema de{" "}
            <span className="text-secondary italic">Arquetipos Internos</span>
          </h1>
          <p className="font-body text-primary-200">
            Generado el {new Date(resultado.fecha).toLocaleDateString('es-ES', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </p>
        </motion.div>

        {/* Constellation Visualization */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-2xl p-6 sm:p-8 mb-8"
        >
          <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2">
            <Star className="text-secondary" size={24} />
            Tu Constelación Interior
          </h2>
          <div className="h-[400px] w-full">
            <ConstellationChart 
              arquetipos={resultado.arquetipos_principales}
              arquetiposData={arquetiposData}
              conflictos={resultado.conflictos_principales}
            />
          </div>
        </motion.section>

        {/* Self Level */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-2xl p-6 sm:p-8 mb-8"
        >
          <h2 className="font-heading text-2xl text-white mb-4 flex items-center gap-2">
            <Sparkles className="text-yellow-400" size={24} />
            Acceso al Self
          </h2>
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <span className="font-body text-primary-200">Nivel actual</span>
              <span className="font-body text-yellow-400 font-semibold">{resultado.nivel_self}%</span>
            </div>
            <Progress value={resultado.nivel_self} className="h-3 bg-primary-950" />
          </div>
          <p className="font-body text-sm text-primary-300">
            {resultado.nivel_self >= 60 
              ? "Tienes buen acceso a tu Self. Puedes observar tus arquetipos con relativa calma y claridad, sin fusionarte completamente con ellos."
              : resultado.nivel_self >= 40
              ? "Tu acceso al Self es moderado. A veces logras observar tus partes con curiosidad, otras veces te fusionas con ellas, especialmente bajo estrés."
              : "Tu acceso al Self necesita desarrollo. Tiendes a fusionarte con tus arquetipos reactivos. Las prácticas de mindfulness y diálogo interior pueden ayudarte."
            }
          </p>
        </motion.section>

        {/* Main Archetypes */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2">
            <Heart className="text-primary-300" size={24} />
            Tus Arquetipos Principales
          </h2>
          <div className="space-y-4">
            {resultado.arquetipos_principales.map((arq, index) => {
              const arqData = arquetiposData[arq.arquetipo];
              if (!arqData) return null;
              const isExpanded = expandedSections[arq.arquetipo];

              return (
                <Collapsible key={arq.arquetipo} open={isExpanded} onOpenChange={() => toggleSection(arq.arquetipo)}>
                  <div 
                    className="glass-card rounded-xl overflow-hidden"
                    style={{ borderLeft: `4px solid ${ARCHETYPE_COLORS[arq.arquetipo]}` }}
                  >
                    <CollapsibleTrigger className="w-full p-6 text-left" data-testid={`archetype-trigger-${arq.arquetipo}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-3xl">{ARCHETYPE_ICONS[arq.arquetipo]}</span>
                          <div>
                            <h3 className="font-heading text-xl text-white">
                              {arqData.nombre}
                            </h3>
                            <p className="font-body text-sm text-primary-300">
                              {arqData.nombreIngles}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-body text-sm text-primary-200">
                              {arq.nivel === 'muy_alto' ? 'Muy Alto' : arq.nivel === 'alto' ? 'Alto' : 'Moderado'}
                            </p>
                            <p className="font-body text-lg text-secondary font-semibold">
                              {arq.porcentaje}%
                            </p>
                          </div>
                          {isExpanded ? <ChevronUp className="text-primary-300" /> : <ChevronDown className="text-primary-300" />}
                        </div>
                      </div>
                      <div className="mt-4">
                        <Progress 
                          value={arq.porcentaje} 
                          className="h-2 bg-primary-950"
                          style={{ '--progress-color': ARCHETYPE_COLORS[arq.arquetipo] }}
                        />
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="px-6 pb-6 space-y-6 border-t border-white/10 pt-6">
                        {/* Description */}
                        <div>
                          <h4 className="font-heading text-lg text-white mb-2">Descripción</h4>
                          <p className="font-body text-sm text-primary-200 leading-relaxed">
                            {arqData.descripcion}
                          </p>
                        </div>

                        {/* Origin */}
                        <div>
                          <h4 className="font-heading text-lg text-white mb-2">Origen</h4>
                          <p className="font-body text-sm text-primary-200 leading-relaxed">
                            {arqData.origen}
                          </p>
                        </div>

                        {/* Typical Phrases */}
                        <div>
                          <h4 className="font-heading text-lg text-white mb-2">Su Voz</h4>
                          <div className="flex flex-wrap gap-2">
                            {arqData.fraseTipica?.map((frase, i) => (
                              <span 
                                key={i}
                                className="bg-primary-950/50 text-primary-200 px-3 py-1 rounded-full text-sm font-body italic"
                              >
                                "{frase}"
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Gifts & Shadows */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-secondary/10 rounded-lg p-4">
                            <h4 className="font-heading text-lg text-secondary mb-2">Dones</h4>
                            <ul className="space-y-1">
                              {arqData.dones?.map((don, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm font-body text-primary-200">
                                  <CheckCircle2 className="text-secondary shrink-0 mt-0.5" size={14} />
                                  {don}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="bg-accent/10 rounded-lg p-4">
                            <h4 className="font-heading text-lg text-accent-glow mb-2">Sombras</h4>
                            <ul className="space-y-1">
                              {arqData.sombras?.map((sombra, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm font-body text-primary-200">
                                  <AlertTriangle className="text-accent-glow shrink-0 mt-0.5" size={14} />
                                  {sombra}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Neuroscience */}
                        <div className="bg-primary-950/50 rounded-lg p-4">
                          <h4 className="font-heading text-lg text-primary-300 mb-2">Neurociencia</h4>
                          <p className="font-body text-sm text-primary-200 leading-relaxed">
                            {arqData.neurociencia}
                          </p>
                        </div>

                        {/* Link to full archetype */}
                        <Link 
                          to={`/arquetipo/${arq.arquetipo}`}
                          className="inline-flex items-center gap-2 text-secondary hover:text-secondary/80 font-body text-sm transition-colors"
                        >
                          <BookOpen size={16} />
                          Ver prácticas de armonización
                        </Link>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        </motion.section>

        {/* Conflicts */}
        {resultado.conflictos_principales?.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="glass-card rounded-2xl p-6 sm:p-8 mb-8"
          >
            <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2">
              <AlertTriangle className="text-accent-glow" size={24} />
              Conflictos Internos Identificados
            </h2>
            <div className="space-y-4">
              {resultado.conflictos_principales.map((conflicto, index) => {
                const arq1 = arquetiposData[conflicto.polo1];
                const arq2 = arquetiposData[conflicto.polo2];
                
                return (
                  <div 
                    key={index}
                    className="bg-accent/5 border border-accent/20 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{ARCHETYPE_ICONS[conflicto.polo1]}</span>
                      <span className="text-accent-glow font-heading">vs</span>
                      <span className="text-xl">{ARCHETYPE_ICONS[conflicto.polo2]}</span>
                      <span className="ml-auto text-xs font-body text-accent-glow uppercase">
                        {conflicto.intensidad}
                      </span>
                    </div>
                    <h4 className="font-heading text-lg text-white mb-1">
                      {arq1?.nombre || conflicto.polo1} vs {arq2?.nombre || conflicto.polo2}
                    </h4>
                    <p className="font-body text-sm text-primary-200">
                      {conflicto.descripcion}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* Actions */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="glass-card rounded-2xl p-6 sm:p-8"
        >
          <h2 className="font-heading text-2xl text-white mb-6">Guardar y Compartir</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Button
              onClick={generatePDF}
              disabled={generatingPDF}
              className="bg-primary hover:bg-primary/90 text-white rounded-full"
              data-testid="download-pdf-button"
            >
              {generatingPDF ? (
                <Loader2 className="mr-2 animate-spin" size={16} />
              ) : (
                <Download className="mr-2" size={16} />
              )}
              Descargar PDF
            </Button>

            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-secondary/50 text-secondary hover:bg-secondary/10 rounded-full"
                  data-testid="save-email-button"
                >
                  <Mail className="mr-2" size={16} />
                  Guardar con Email
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-white/10">
                <DialogHeader>
                  <DialogTitle className="font-heading text-white">Guardar Resultado</DialogTitle>
                  <DialogDescription className="text-primary-300">
                    Ingresa tu email para guardar tu resultado y poder acceder a él en el futuro.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-primary-950/50 border-white/10"
                  />
                  <Button
                    onClick={handleSaveResult}
                    disabled={saving}
                    className="w-full bg-primary hover:bg-primary/90 text-white"
                  >
                    {saving ? (
                      <Loader2 className="mr-2 animate-spin" size={16} />
                    ) : (
                      <CheckCircle2 className="mr-2" size={16} />
                    )}
                    Guardar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* CTA for deeper work */}
          <div className="bg-primary-950/50 rounded-xl p-6 text-center">
            <h3 className="font-heading text-xl text-white mb-2">
              ¿Quieres profundizar en tu trabajo interior?
            </h3>
            <p className="font-body text-sm text-primary-300 mb-4">
              Sesiones 1:1 de trabajo con arquetipos guiadas por Julián López
            </p>
            <a 
              href="mailto:agenda@alquimiadelalma.click?subject=Sesión de Arquetipos"
              className="inline-block"
            >
              <Button className="btn-mystical rounded-full">
                Agendar Sesión
              </Button>
            </a>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
